const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

export function crc32(bytes: Uint8Array): number {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array | null> {
    if (typeof CompressionStream === 'undefined') return null;
    try {
        const stream = new Blob([bytes as BlobPart])
            .stream()
            .pipeThrough(new CompressionStream('deflate-raw'));
        return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch {
        return null;
    }
}

interface ZipEntry {
    name: Uint8Array;
    crc: number;
    packedSize: number;
    size: number;
    method: number;
    offset: number;
}

const UTF8_FLAG = 0x0800;
const DOS_DATE = ((2026 - 1980) << 9) | (1 << 5) | 1;

function header(
    signature: number,
    entry: ZipEntry,
    central: boolean
): Uint8Array {
    const bytes = new Uint8Array((central ? 46 : 30) + entry.name.length);
    const view = new DataView(bytes.buffer);
    let at = 0;
    const u16 = (v: number) => {
        view.setUint16(at, v, true);
        at += 2;
    };
    const u32 = (v: number) => {
        view.setUint32(at, v, true);
        at += 4;
    };
    u32(signature);
    if (central) u16(20);
    u16(20);
    u16(UTF8_FLAG);
    u16(entry.method);
    u16(0);
    u16(DOS_DATE);
    u32(entry.crc);
    u32(entry.packedSize);
    u32(entry.size);
    u16(entry.name.length);
    u16(0);
    if (central) {
        u16(0);
        u16(0);
        u16(0);
        u32(0);
        u32(entry.offset);
    }
    bytes.set(entry.name, at);
    return bytes;
}

export class ZipWriter {
    private parts: Uint8Array[] = [];
    private entries: ZipEntry[] = [];
    private offset = 0;

    async add(name: string, text: string): Promise<void> {
        const raw = new TextEncoder().encode(text);
        const packed = await deflateRaw(raw);
        const useDeflate = packed !== null && packed.length < raw.length;
        const body = useDeflate ? packed : raw;
        const entry: ZipEntry = {
            name: new TextEncoder().encode(name),
            crc: crc32(raw),
            packedSize: body.length,
            size: raw.length,
            method: useDeflate ? 8 : 0,
            offset: this.offset,
        };
        const local = header(0x04034b50, entry, false);
        this.parts.push(local, body);
        this.offset += local.length + body.length;
        this.entries.push(entry);
    }

    finish(): Blob {
        const central = this.entries.map(e => header(0x02014b50, e, true));
        const centralSize = central.reduce((sum, c) => sum + c.length, 0);
        const end = new Uint8Array(22);
        const view = new DataView(end.buffer);
        view.setUint32(0, 0x06054b50, true);
        view.setUint16(8, this.entries.length, true);
        view.setUint16(10, this.entries.length, true);
        view.setUint32(12, centralSize, true);
        view.setUint32(16, this.offset, true);
        return new Blob([...this.parts, ...central, end] as BlobPart[], {
            type: 'application/zip',
        });
    }
}
